import {
  AsyncAutocompleteWithAdd,
  AsyncCallArgs,
  FormSection,
  GridContainer,
  GridItem,
  handleFormSubmission,
  InputFormField,
  isArrayNotEmpty,
  isNotEmpty,
  SubmitFormButton,
  useAsyncCall,
  useReactHookForm,
  ViewDialog,
} from "@duosoftbg/nacid-components";
import { FormProvider } from "react-hook-form";
import { createAcPage, editAcPage, getRolesAutocomplete } from "../../../../../../../../axios/api/services";
import RolesDialogList from "../../../../../groupsAndRoles/dialog/parts/RolesDialogList";
import { Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../../../../../../../hooks/redux/base/useAppDispatch";
import useAppSelector from "../../../../../../../../hooks/redux/base/useAppSelector";
import { adminConsoleSection } from "../../../../../../../../utils/constants";
import { AdminConsolePageDetails } from "../../../../../../../../types/adminConsoleTypes";
import { acPageCreationInitialValues } from "../../../../../../../../init/adminConsoleCreationInitialValues";
import { closeEditModal } from "../../../../../../../../store/redux/slice/ComponentsControl/adminConsoleConfigControl";
import { renewAcCache } from "../../../../../../../../store/redux/slice/AppData/aConsoleData";
import { toast } from "react-toastify";
import { createPageValidationSchema } from "../../../../../../../../yup/schema/adminConsole/adminConsoleValidationSchema";

const AdminConsolePageDialog = () => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState([]);

  const { open, object } = useAppSelector((state) => {
    return state.ComponentsControl.acConfigControl.sections[adminConsoleSection.page].modals.edit;
  });

  const isEdit = isNotEmpty(object?.id);

  const { methods } = useReactHookForm<AdminConsolePageDetails>({
    defaultValues: undefined,
    validationSchema: createPageValidationSchema,
  });

  useEffect(() => {
    if (object) {
      methods.reset(object);
      if (open && object.id) {
        let roles = isArrayNotEmpty(object.accessRoles)
          ? object.accessRoles.map((role) => ({ id: role, name: role }))
          : [];
        setRoles(roles);
      }
    }
  }, [methods, object, open]);

  const handleCloseDialog = () => {
    methods.reset(acPageCreationInitialValues);
    setRoles([]);
    dispatch(closeEditModal({ section: adminConsoleSection.page }));
  };

  const onSubmit = (values) => {
    const submitPage: AsyncCallArgs = {
      promise: isEdit ? editAcPage(values, roles) : createAcPage(values, roles),
      reactHooksForm: { methods },
      onSuccess: (response) => {
        dispatch(renewAcCache(response));
        handleCloseDialog();
        toast.success(isEdit ? t("m.ac.page.update.success") : t("m.ac.page.create.success"));
      },
    };
    asyncCall(submitPage);
  };

  const handleAddRole = (role) => {
    if (!roles.find((arrayRole) => arrayRole.name === role.name)) {
      setRoles([...roles, { id: role.name, name: role.name }]);
    }
  };

  const handleRemoveRoleClick = (role) => {
    setRoles(roles.filter((arrayRole) => arrayRole.id !== role.id));
  };

  return (
    <ViewDialog
      open={open}
      onClose={handleCloseDialog}
      title={isEdit ? "t.ac.page.update.dialog" : "t.ac.page.create.dialog"}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmission({ methods, onSubmitFn: onSubmit, t })}>
          {isEdit && (
            <GridContainer spacing={3}>
              <GridItem sm={12} md={4}>
                <InputFormField isDisabled={true} required={true} fieldName={"id"} labelCode={"l.input.ac.id"} />
              </GridItem>
            </GridContainer>
          )}

          <FormSection label={"t.main.details"}>
            <GridContainer spacing={3} mt={0}>
              <GridItem sm={12} md={4}>
                <InputFormField required={true} fieldName={"title"} labelCode={"l.input.ac.page.title"} />
              </GridItem>
              <GridItem sm={12} md={4}>
                <InputFormField required={true} fieldName={"titleEn"} labelCode={"l.input.ac.page.title.en"} />
              </GridItem>
              <GridItem sm={12} md={4}>
                <InputFormField required={true} fieldName={"position"} labelCode={"l.input.ac.position"} />
              </GridItem>
              <GridItem sm={12} md={4}>
                <InputFormField fieldName={"href"} labelCode={"l.input.ac.href"} />
              </GridItem>
            </GridContainer>
          </FormSection>

          <FormSection label={"t.ac.roles"}>
            <GridContainer spacing={3} mt={0}>
              <AsyncAutocompleteWithAdd
                onAddRecord={handleAddRole}
                setOptionText={(option) => option.name}
                getOptionLabel={(option) => option.id}
                setInputOnSelect={(option) => option.name}
                autocompleteFn={getRolesAutocomplete}
                label={"l.select.role"}
              />

              <RolesDialogList roles={roles} isEdit={true} onRemove={handleRemoveRoleClick} />
            </GridContainer>
          </FormSection>

          <GridContainer spacing={3} mt={0}>
            <GridItem sm={12} md={12}>
              <Typography align={"right"}>
                <SubmitFormButton label={"l.btn.submit"} color={"success"} />
              </Typography>
            </GridItem>
          </GridContainer>
        </form>
      </FormProvider>
    </ViewDialog>
  );
};
export default AdminConsolePageDialog;
